'use client';

// ClarificationSummary - Summary shown when all questions are resolved

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslations } from 'next-intl';
import type { ClarificationSummaryProps } from './types';

/**
 * ClarificationSummary - Display when all clarification questions are resolved
 *
 * Features:
 * - Count of questions answered vs skipped
 * - Total characters added from answers
 * - Proceed to testing button
 * - Celebration message
 * - RTL support via CSS logical properties
 */
export function ClarificationSummary({ summary, onProceed }: ClarificationSummaryProps) {
  const t = useTranslations('creator.clarification.summary');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        textAlign: 'center',
        backgroundColor: 'success.light',
        borderRadius: 2,
      }}
    >
      {/* Success icon */}
      <CheckCircleIcon
        sx={{
          fontSize: 64,
          color: 'success.main',
          mb: 2,
        }}
      />

      {/* Title */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {t('title')}
      </Typography>

      {/* Ready message */}
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t('readyMessage')}
      </Typography>

      {/* Statistics */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {/* Questions answered */}
        <Box>
          <Typography variant="h4" fontWeight="bold" color="success.dark">
            {summary.answered}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('questionsAnswered', { answered: summary.answered })}
          </Typography>
        </Box>

        {/* Questions skipped */}
        {summary.skipped > 0 && (
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.secondary">
              {summary.skipped}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('questionsSkipped', { skipped: summary.skipped })}
            </Typography>
          </Box>
        )}

        {/* Characters added */}
        {summary.totalCharactersAdded > 0 && (
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {summary.totalCharactersAdded.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('charactersAdded', { count: summary.totalCharactersAdded })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Proceed button */}
      <Button
        variant="contained"
        size="large"
        onClick={onProceed}
        startIcon={<PlayArrowIcon />}
        sx={{
          minWidth: 200,
          py: 1.5,
        }}
      >
        {t('proceedToTesting')}
      </Button>
    </Paper>
  );
}

export default ClarificationSummary;
